export interface CustomApiV2Error {
    statusCode: number
    success: false
    errors: {
        code: number
        field: string | null
        msg: string
    }[]
}

export type NavItemsState = 'default' | 'optional' | 'active' | 'error' | 'success' | 'disabled'

export type ApplyV2SettingsSectionName =
    | 'cv'
    | 'profile'
    | 'course'
    | 'driverLicence'
    | 'education'
    | 'language'
    | 'reference'
    | 'skill'
    | 'workExperience'
    | 'reviewApply'
    | 'question'

interface BaseSection {
    visible: boolean
    required: boolean
    order: number
}

interface SimpleSection extends BaseSection {
    name: Exclude<ApplyV2SettingsSectionName, 'reviewApply'>
}

interface ReviewAndApplyFieldBase<TName extends string, TOptions> extends BaseSection {
    name: TName
    options: TOptions
}

export type ReviewAndApplyFieldAnonymousReason = ReviewAndApplyFieldBase<
    'anonymousReason',
    {
        maxLength: number
        minLength: number
    }
>

export type ReviewAndApplyFieldApplicationText = ReviewAndApplyFieldBase<
    'applicationText',
    {
        maxLength: number
        minLength: number
    }
>

export type ReviewAndApplyFieldAttachments = ReviewAndApplyFieldBase<
    'attachments',
    {
        maxFiles: number
        descriptionText: number
    }
>

export type ReviewAndApplyField =
    | ReviewAndApplyFieldAnonymousReason
    | ReviewAndApplyFieldApplicationText
    | ReviewAndApplyFieldAttachments

export interface ReviewApplyFieldsMap {
    anonymousReason: ReviewAndApplyFieldAnonymousReason
    applicationText: ReviewAndApplyFieldApplicationText
    attachments: ReviewAndApplyFieldAttachments
}

export interface ReviewApplySection extends BaseSection {
    name: 'reviewApply'
    fields: ReviewApplyFieldsMap
}

export type ApplyV2SettingsSectionCv = BaseSection & {
    name: 'cv'
}

export type ApplyV2SettingsSectionProfile = BaseSection & {
    name: 'profile'
    fields: {
        firstName: BaseSection & {
            name: 'firstName'
        }
        lastName: BaseSection & {
            name: 'lastName'
        }
        email: BaseSection & {
            name: 'email'
        }
        mobilePhone: BaseSection & {
            name: 'mobilePhone'
        }
        dateOfBirth: BaseSection & {
            name: 'dateOfBirth'
        }
        gender: BaseSection & {
            name: 'gender'
        }
        nationality: BaseSection & {
            name: 'nationality'
        }
        title: BaseSection & {
            name: 'title'
        }
        address: BaseSection & {
            name: 'address'
        }
        linkedin: BaseSection & {
            name: 'linkedin'
        }
    }
}

export type ApplyV2SettingsSectionCourse = BaseSection & {
    name: 'course'
}
export type ApplyV2SettingsSectionDriverLicense = BaseSection & {
    name: 'driverLicence'
}

export type ApplyV2SettingsSectionEducation = BaseSection & {
    name: 'education'
}

export type ApplyV2SettingsSectionReviewApply = BaseSection & {
    name: 'reviewApply'
    fields: ReviewApplyFieldsMap
}

export type ApplyV2SettingsSectionQuestion = BaseSection & {
    name: 'question'
}

export type ApplyV2SettingsSectionLanguage = BaseSection & {
    name: 'language'
}

export type ApplyV2SettingsSectionReference = BaseSection & {
    name: 'reference'
}
export type ApplyV2SettingsSectionSkill = BaseSection & {
    name: 'skill'
}
export type ApplyV2SettingsSectionWorkExperience = BaseSection & {
    name: 'workExperience'
}

export type ApplyV2SettingsSection =
    | ApplyV2SettingsSectionCv
    | ApplyV2SettingsSectionProfile
    | ApplyV2SettingsSectionCourse
    | ApplyV2SettingsSectionDriverLicense
    | ApplyV2SettingsSectionEducation
    | ApplyV2SettingsSectionLanguage
    | ApplyV2SettingsSectionReference
    | ApplyV2SettingsSectionSkill
    | ApplyV2SettingsSectionWorkExperience
    | ApplyV2SettingsSectionReviewApply
    | ApplyV2SettingsSectionQuestion

export type ApplyV2SettingsSections = {
    cv?: ApplyV2SettingsSectionCv
    profile?: ApplyV2SettingsSectionProfile
    course?: ApplyV2SettingsSectionCourse
    driverLicence?: ApplyV2SettingsSectionDriverLicense
    education?: ApplyV2SettingsSectionEducation
    language?: ApplyV2SettingsSectionLanguage
    reference?: ApplyV2SettingsSectionReference
    skill?: ApplyV2SettingsSectionSkill
    workExperience?: ApplyV2SettingsSectionWorkExperience
    reviewApply?: ApplyV2SettingsSectionReviewApply
    question?: ApplyV2SettingsSectionQuestion
}

export interface GetApplyV2SettingsResponse {
    statusCode: number
    externalLink: string | null
    candidateId: number | null
    application: null
    settings: []
    cvParserSettings: {
        isEnabledCorporation: true
        isEnabledSetting: true
        isAvailable: true
    }

    appearance: {
        type: number
        color: string | null
        image: string | null
    }
    visitorUid: string | null
    unfinishedApplicationUid: any
    steps: ApplyV2SettingsSections
    corporationSettings: { courseExpiryMandatory: boolean }
}

export interface ApplyV2TempProfileCvData {
    uid: string
    existingId: null | string
    name: string
    ext: string
    uploaded: string
    downloadUrl: string
    size: number
    parsed: boolean
    allowDelete: boolean
    type: number // TODO - is enum?
}

export interface ApplyV2TempAddress {
    country?: string | null
    city?: string | null
    postalCode?: string | null
    address1?: string | null
    address2?: string | null
}

export type GenderType = 0 | 1 | 2 | 3 | 4 | 5

export interface ApplyV2TempPhone {
    prefix: string
    number: string
}

export interface ApplyV2TempProfileProfileData {
    firstName: string
    lastName: string
    email: string | null
    title: string | null
    gender: GenderType | null
    address: ApplyV2TempAddress | null
    mobilePhone: ApplyV2TempPhone | null
    linkedin: string | null
    nationality: string | null
    dateOfBirth: string | null
}

export interface ApplyV2TempEducation {
    uid: string
    existingId: string | null
    schoolName: string | null
    degree: string | null
    location: string | null
    fromDate: string | null
    toDate: string | null
    description: string | null
    educationListId: number
    educationType: string | null
}

export interface ApplyV2TempWorkExperience {
    uid: string
    existingId: string | null
    title: string | null
    companyName: string | null
    location: string | null
    fromDate: string | null
    toDate: string | null
    description: string | null
    titleListId: number
    current: boolean | null
}

export interface ApplyV2TempReference {
    uid: string
    existingId: string | null
    title: string | null
    name: string | null
    companyName: string | null
    email: string | null
    description: string | null
    mobilePhone: ApplyV2TempPhone | null
}

export interface ApplyV2TempLanguage {
    uid: string
    existingId: string | null
    languageLevelId: number // TODO - is enum?
    worldLanguageId: number // TODO - is enum?
}

export interface ApplyV2TempSkill {
    uid: string
    existingId: string | null
    skill: string | null
    rating: number // TODO - is enum?
    skillTagId: number
}

// export interface ApplyV2TempCourseFile {
//     uid: string
//     existingId: string | null
//     name: string
//     ext: string
//     uploaded: string
//     downloadUrl: string
//     size: number
//     parsed: boolean
//     allowDelete: boolean
//     type: number // TODO - is enum?
// }

export interface ApplyV2TempCourseFile extends ApplyV2FileAttachment {
    type: number
    existingId: string | null
    downloadUrl: string
    parsed: boolean
    allowDelete: boolean
}

export interface ApplyV2TempBase64File {
    name: string
    ext: string
    base64: string
    lastModified: number
    size: number
}

export interface ApplyV2TempCourse {
    uid: string
    existingId: string | null
    courseName: string | null
    courseDescription: string | null
    startDate: string | null
    endDate: string | null
    courseListId: number
    verified: boolean
    files: ApplyV2TempCourseFile[] | null
}

export interface ApplyV2TempProfileData {
    adId: number
    anonymousReason: string | null
    applicationText: string | null
    attachments: ApplyV2TempProfileCvData[] | null
    candidateId: number
    uid: string
    visitorUid: string
    status: number
    cvStatus: number
    cv: ApplyV2TempProfileCvData
    profile: ApplyV2TempProfileProfileData
    education: ApplyV2TempEducation[] | null
    workExperience: ApplyV2TempWorkExperience[] | null
    reference: ApplyV2TempReference[] | null
    language: ApplyV2TempLanguage[] | null
    skill: ApplyV2TempSkill[] | null
    driverLicence: number[] | null // TODO - is enum?
    course: ApplyV2TempCourse[] | null
    noFields: ApplyV2SettingsSectionName[]
    step: ApplyV2SettingsSectionName | null
}

export interface ApplyV2BaseResponse<T = unknown> {
    statusCode: number
    success: boolean
    message: string
    data?: T
    errors?: any[]
}

export type GetApplyV2ProfileResponse = ApplyV2BaseResponse<ApplyV2TempProfileData>

export interface V2CourseFile {
    allowDelete?: boolean
    downloadUrl?: string
    existingId?: string | number | null
    ext: string
    name: string
    parsed?: boolean
    size: number
    type?: number
    uid: string
    uploaded?: string // Date string
}

export interface ApplyV2FileAttachment {
    ext: string
    uid: string
    name: string
    size: number
    uploaded: string
}

export interface ApplyV2CustomFieldProperties {
    decimals?: number
    min?: number
    max?: number
    numberFormat?: number
    currencyId?: number
}

export const ApplyV2CustomFieldTypeConst = {
    SingleSelect: 1,
    MultiSelect: 2,
    Date: 3,
    Text: 4,
    Integer: 5,
} as const

export type ApplyV2CustomFieldType = (typeof ApplyV2CustomFieldTypeConst)[keyof typeof ApplyV2CustomFieldTypeConst]

export interface ApplyV2CustomField {
    customFieldId: number
    description: string
    typeId: ApplyV2CustomFieldType
    properties?: ApplyV2CustomFieldProperties
}

export interface ApplyV2AttributeDropdown {
    id: number
    name: string
}

export interface ApplyV2AttributeCheckbox {
    id: number
    name: string
}

export interface ApplyV2AttributeSettings {
    bool: boolean
    rating: boolean
    text: boolean
    shortText: boolean
    checkbox: boolean
    checkboxRating: boolean
    dropdown: boolean
    date: boolean
    dropdowns?: ApplyV2AttributeDropdown[]
    checkboxes?: ApplyV2AttributeCheckbox[]
}

export interface ApplyV2AttributeValue {
    bool?: boolean
    date?: string
    text?: string
    rating?: number
    checkbox?: boolean | Array<{ id: number; rating?: number }>
    checkboxes?: Array<{ id: number; rating?: number; name: string; checked: boolean }>
    dropdown?: number
    dropdowns?: { id: number; name: string }[]
    shortText?: string
}

export interface ApplyV2BaseQuestion {
    questionId: number
    name: string
    type: string
    required: boolean
    orderId: number
    parentQuestionId: number
    parentOptionId: number
    visible: boolean
    answer: {
        questionId: number
        value: any
    } | null
    children?: ApplyV2Question[]
    settings?: {
        multiple?: boolean
        message?: string
        trigger?: boolean
    }
}

interface ApplyV2BaseCustomFieldQuestion<TType extends ApplyV2CustomFieldType, TValue> extends ApplyV2BaseQuestion {
    type: 'customField'
    customField: {
        properties?: any
        customFieldId: number
        description: string
        typeId: TType
    }
    options?: { id: number; name: string }[]
    answer: {
        questionId: number
        value: TValue
    } | null
}

export interface ApplyV2SingleSelectQuestion extends ApplyV2BaseQuestion {
    type: 'singleSelect'
    options: { id: number; name: string }[]
}

export interface ApplyV2MultiSelectQuestion extends ApplyV2BaseQuestion {
    type: 'multiSelect'
    options: { id: number; name: string }[]
}

export interface ApplyV2TextQuestion extends ApplyV2BaseQuestion {
    type: 'text'
    answer: {
        questionId: number
        value: string
    } | null
}

export interface ApplyV2LongTextQuestion extends ApplyV2BaseQuestion {
    type: 'longText'
    answer: {
        questionId: number
        value: string
    } | null
}

export interface ApplyV2YesNoQuestion extends ApplyV2BaseQuestion {
    type: 'yesNo'
    answer: {
        questionId: number
        value: boolean
    } | null
}

export interface ApplyV2StarRatingQuestion extends ApplyV2BaseQuestion {
    type: 'starRating'
    answer: {
        questionId: number
        value: number | null
    } | null
}

export interface ApplyV2DateQuestion extends ApplyV2BaseQuestion {
    type: 'date'
    answer: {
        questionId: number
        value: string | null
    } | null
}

export interface ApplyV2DateRangeQuestion extends ApplyV2BaseQuestion {
    type: 'dateRange'
    answer: {
        questionId: number
        value: [string, string] | null
    } | null
}

export interface ApplyV2NumberQuestion extends ApplyV2BaseQuestion {
    type: 'number'
    answer: {
        questionId: number
        value: number
    } | null
}

export interface ApplyV2AttachmentQuestion extends ApplyV2BaseQuestion {
    type: 'attachment'
    answer: {
        questionId: number
        value: ApplyV2FileAttachment[]
    } | null
    settings: {
        multiple: boolean
    }
}

export interface ApplyV2QualifyingQuestion extends ApplyV2BaseQuestion {
    type: 'qualifying'
    settings: {
        message: string
        trigger: boolean
    }
}

// export interface ApplyV2CustomFieldQuestion extends ApplyV2BaseQuestion {
//     type: 'customField'
//     customField: ApplyV2CustomField
//     answer: {
//         questionId: number
//         value: number
//     } | null
// }

// export interface ApplyV2CustomFieldSingleSelectQuestion extends ApplyV2BaseQuestion {
//     type: 'customField'
//     customField: {
//         customFieldId: number
//         description: string
//         typeId: typeof ApplyV2CustomFieldTypeConst.SingleSelect
//     }
//     answer: {
//         questionId: number
//         value: number
//     } | null
//     options: { id: number; name: string }[]
// }

export interface ApplyV2CustomFieldSingleSelectQuestion
    extends ApplyV2BaseCustomFieldQuestion<typeof ApplyV2CustomFieldTypeConst.SingleSelect, number | null> {
    options: { id: number; name: string }[]
}

// export interface ApplyV2CustomFieldMultiSelectQuestion extends ApplyV2BaseQuestion {
//     type: 'customField'
//     customField: {
//         customFieldId: number
//         description: string
//         typeId: typeof ApplyV2CustomFieldTypeConst.MultiSelect
//     }
//     answer: {
//         questionId: number
//         value: number
//     } | null
//     options: { id: number; name: string }[]
// }

export interface ApplyV2CustomFieldMultiSelectQuestion
    extends ApplyV2BaseCustomFieldQuestion<typeof ApplyV2CustomFieldTypeConst.MultiSelect, number[] | null> {
    options: { id: number; name: string }[]
}

// export interface ApplyV2CustomFieldDateQuestion extends ApplyV2BaseQuestion {
//     type: 'customField'
//     customField: {
//         customFieldId: number
//         description: string
//         typeId: typeof ApplyV2CustomFieldTypeConst.Date
//     }
//     answer: {
//         questionId: number
//         value: number
//     } | null
// }

export interface ApplyV2CustomFieldDateQuestion
    extends ApplyV2BaseCustomFieldQuestion<typeof ApplyV2CustomFieldTypeConst.Date, string | null> {}

// export interface ApplyV2CustomFieldTextQuestion extends ApplyV2BaseQuestion {
//     type: 'customField'
//     customField: {
//         customFieldId: number
//         description: string
//         typeId: typeof ApplyV2CustomFieldTypeConst.Text
//     }
//     answer: {
//         questionId: number
//         value: number
//     } | null
// }

export interface ApplyV2CustomFieldTextQuestion
    extends ApplyV2BaseCustomFieldQuestion<typeof ApplyV2CustomFieldTypeConst.Text, string | null> {}

// export interface ApplyV2CustomFieldIntegerQuestion extends ApplyV2BaseQuestion {
//     type: 'customField'
//     customField: {
//         customFieldId: number
//         description: string
//         typeId: typeof ApplyV2CustomFieldTypeConst.Integer
//     }
//     answer: {
//         questionId: number
//         value: number
//     } | null
// }

export interface ApplyV2CustomFieldIntegerQuestion
    extends ApplyV2BaseCustomFieldQuestion<typeof ApplyV2CustomFieldTypeConst.Integer, number | null> {}

export interface ApplyV2AttributeQuestion extends ApplyV2BaseQuestion {
    type: 'attribute'
    attribute: ApplyV2AttributeSettings
    answer: {
        questionId: number
        value: ApplyV2AttributeValue
    } | null
}

export type ApplyV2Question =
    | ApplyV2SingleSelectQuestion
    | ApplyV2MultiSelectQuestion
    | ApplyV2TextQuestion
    | ApplyV2LongTextQuestion
    | ApplyV2YesNoQuestion
    | ApplyV2StarRatingQuestion
    | ApplyV2DateQuestion
    | ApplyV2DateRangeQuestion
    | ApplyV2NumberQuestion
    | ApplyV2AttachmentQuestion
    | ApplyV2QualifyingQuestion
    | ApplyV2CustomFieldSingleSelectQuestion
    | ApplyV2CustomFieldMultiSelectQuestion
    | ApplyV2CustomFieldDateQuestion
    | ApplyV2CustomFieldTextQuestion
    | ApplyV2CustomFieldIntegerQuestion
    | ApplyV2AttributeQuestion

export interface ApplyV2ApiError {
    statusCode: number
    success: boolean
    errors: Array<{
        code: number
        field: string | null
        msg: string
    }>
}

export type GetAllQuestionsV2Response = ApplyV2BaseResponse<{
    questions: ApplyV2Question[]
}>
